import React, {useEffect, useState} from "react";
import {Box, makeStyles, Tab, Tabs, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton} from "@material-ui/core";
import {useForm} from "react-hook-form";
import {useHistory, useParams} from "react-router-dom";
import EditIcon from "@material-ui/icons/Edit";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import Button from "@material-ui/core/Button";
import apiHelper from "../../../../util/ApiHelper";
import {useLoading} from "../../../../util/LoadingProvider";
import EmailTemplateSubmissionForm from "./EmailTemplateSubmissionForm";
import {toast} from "react-toastify";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";

const EmailType = {
  CONFIRMATION_BLUEBIRD: 'confirmation-bluebird',
  CONFIRMATION_FIREBIRD_GOLDCREST: 'confirmation-firebird-goldcrest',
  CONFIRMATION_UPGRADE: 'confirmation-upgrade',
  RENEWAL_REMINDER: 'renewal-reminder',
  CONFIRMATION_RENEWAL: 'confirmation-renewal',
  CONFIRMATION_DOWNGRADE: 'confirmation-downgrade',
  FAILURE: 'failure',
  RESET_PASSWORD: 'reset-password',
  REDEMPTION_COLLECTION: 'redemption-collection',
  CONFIRMATION_EMAIL_VERIFIED: 'confirmation-email-verified',
  CONFIRMATION_OTP: 'otp',
  // CONFIRMATION_DIGITAL_CARD: 'confirmation-digital-card',
  // CONFIRMATION_PHYSICAL_CARD: 'confirmation-physical-card',
  // IMPORT_CONFIRMATION: 'import-confirmation',
  // RENEWAL_CONFIRMATION_DIGITAL_CARD: 'renewal-confirmation-digital-card',
  // RENEWAL_CONFIRMATION_PHYSICAL_CARD: 'renewal-confirmation-physical-card',
}

const emailTypes = Object.values(EmailType);

const EmailTypeDisplay = {
    [EmailType.CONFIRMATION_BLUEBIRD]: 'Confirmation (Bluebird)',
    [EmailType.CONFIRMATION_FIREBIRD_GOLDCREST]: 'Confirmation (Firebird/ Goldcrest)',
    [EmailType.CONFIRMATION_UPGRADE]: 'Confirmation (Upgrade)',
    [EmailType.RENEWAL_REMINDER]: 'Renewal Reminder',
    [EmailType.CONFIRMATION_RENEWAL]: 'Confirmation (Renewal)',
    [EmailType.CONFIRMATION_DOWNGRADE]: 'confirmation-downgrade',
    [EmailType.FAILURE]: 'Failure',
    [EmailType.RESET_PASSWORD]: 'Reset Password',
    [EmailType.REDEMPTION_COLLECTION]: 'Redemption Collection',
    [EmailType.CONFIRMATION_EMAIL_VERIFIED]: 'Confirmation (Email Verified)',
    [EmailType.CONFIRMATION_OTP]: 'Confirmation (OTP)',

    // [EmailType.CONFIRMATION_DIGITAL_CARD]: 'Confirmation (e-card)',
    // [EmailType.CONFIRMATION_PHYSICAL_CARD]: 'Confirmation (physical card)',
    // [EmailType.IMPORT_CONFIRMATION]: 'Confirmation (Walk-in Members)',
    // [EmailType.RENEWAL_CONFIRMATION_DIGITAL_CARD]: 'Renewal Confirmation (e-card)',
    // [EmailType.RENEWAL_CONFIRMATION_PHYSICAL_CARD]: 'Renewal Confirmation (physical card)',
}

const emailTabs = Object.values(EmailTypeDisplay)

const defaultFormTemplateFactory = () => ({
  id: null,
  subjectTC: '',
  contentTC: '',
  subjectEN: '',
  contentEN: '',
  type: '',
  createTime: ''
});

const defaultFormValues = (() => {
  const values = {};
  for (const key in EmailType) {
    values[EmailType[key]] = defaultFormTemplateFactory();
  }
  return values;
})()

const schemaFactory = () => (
  yup.object().shape({
    subjectEN: yup.string().required("Subject (En) is required"),
    subjectTC: yup.string().required("Subject (Tc) is required"),
  })
)

const schema = (() => {
    const schema = {};
    for (const key in EmailType) {
        schema[EmailType[key]] = schemaFactory();
    }
    return yup.object().shape(schema);
})()

const useStyles = makeStyles((theme) => ({
  textInput: {
    backgroundColor: "white",
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "362px",
    },
  },
  buttonGroup: {
    marginTop: "20px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: "20px 30px 10px",
    background: "#ffffff",
    minHeight: "800px",
  },
  tab: {
    fontSize: "13px",
  },
  tableContainer: {
    marginTop: theme.spacing(3),
    boxShadow: "none",
    border: "1px solid #cccccc",
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
}));

function TabPanel(props) {
  const classes = useStyles();
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      <Box p={3} className={classes.box}>
        {children}
      </Box>
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
}

export default function MemberEmailSettingsPage({
                                                  disabled,
                                                  ...props
                                                }) {
  const history = useHistory();
  const { templateType } = useParams();
  const [defaultValues] = useState(defaultFormValues);
  const [initialValues, setInitialValues] = useState(defaultFormValues);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const {setLoading} = useLoading();

  const classes = useStyles();

  const {
    control,
    handleSubmit,
    reset
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const getMemberEmailTemplate = async () => {
    setLoading(true);
    try {
      const result = await apiHelper.hkiff.getMembershipEmailTemplate();
      const data = result.data;
      const dataObject = {};
      const templates = [];
      data.forEach((item) => {
        if (emailTypes.includes(item.type)) {
          dataObject[item.type] = item;
          templates.push({
            ...item,
            displayType: EmailTypeDisplay[item.type] || item.type
          });
        }
      })
      setInitialValues(dataObject);
      setEmailTemplates(templates);
      reset(dataObject);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    getMemberEmailTemplate();
  }, []);

  const updateMemberEmailSettings = async (data) => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.updateMembershipEmailTemplate(data);
      if (result.data.code === 0) {
        await getMemberEmailTemplate();
        toast.dark("Your changes is saved.");
        if (templateType) {
          // Redirect to list page after editing
          history.push("/cineclub/membership/email");
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEdit = (templateType) => {
    history.push(`/cineclub/membership/email/${templateType}/edit`);
  };

  // If we're in edit mode (templateType is provided), show the edit form
  if (templateType) {
    const template = initialValues[templateType];
    const displayName = EmailTypeDisplay[templateType] || templateType;
    
    if (!template) {
      return <div>Loading...</div>;
    }

    return (
      <div className={classes.content}>
        <Box className={classes.header}>
          <Button
            className={classes.backButton}
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/cineclub/membership/email")}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Edit Email Template: {displayName}
          </Typography>
        </Box>
        <Box className={classes.box}>
          <EmailTemplateSubmissionForm
            name={templateType}
            display={displayName}
            initialValues={initialValues}
            handleSubmit={handleSubmit}
            onSubmit={(formData) => {
              // Extract the specific template data from the form
              const templateData = formData[templateType];
              updateMemberEmailSettings(templateData);
            }}
            control={control}
            classes={classes}
            disabled={disabled}
          />
        </Box>
      </div>
    );
  }

  // Otherwise, show the table view
  return (
    <div className={classes.content}>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
          Member &gt; Email Setting
        </Typography>
      </Box>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Subject (English)</TableCell>
              <TableCell>Subject (繁體中文)</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emailTemplates.map((template) => (
              <TableRow key={template.id || template.type}>
                <TableCell>{template.subjectEN || "-"}</TableCell>
                <TableCell>{template.subjectTC || "-"}</TableCell>
                <TableCell>{template.displayType || template.type}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(template.type)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
